/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useRef } from 'react'
import { useIntl } from 'react-intl'

import { EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiSplitPanel } from '@elastic/eui'
import {
  AreaSeries,
  Axis,
  Chart,
  LineSeries,
  Position,
  ScaleType,
  Settings,
  Tooltip,
  type PointerEvent,
} from '@elastic/charts'

import { MetricTitle } from '@modules/auto-ops-components/MetricTitle/MetricTitle'
import { ChartCallout } from '@modules/auto-ops-components/ChartCallout/ChartCallout'
import { bytesToGigabytes, calculateDomain, generateCallout } from '@modules/autoops-lib/util'
import { type DateRangeOption, timeFormatterFn } from '@modules/autoops-lib/constants'
import type { MetricItem } from '@modules/autoops-api/types'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useElasticChartsTheme } from '@modules/cui/charts/hooks'
import { TruncatedTooltip } from '@modules/auto-ops-components/TruncatedTooltip/TruncatedTooltip'
import { useConfig } from '@modules/cui/ConfigContext'

type ElasticsearchChartsProps = {
  data: Record<string, MetricItem | undefined>
  from: DateRangeOption
}

const yAxisStyle = {
  tickLine: { visible: true, size: 0, padding: 10 },
  tickLabel: {
    alignment: {
      horizontal: Position.Left,
      vertical: Position.Bottom,
    },
    padding: 0,
    offset: { x: 0, y: 0 },
  },
}

const ElasticsearchCharts = ({ data, from }: ElasticsearchChartsProps) => {
  const appName = useConfig('APP_NAME')
  const [isFlagUsable, flags] = useFlagsWhenLoaded()
  const isActivated =
    appName === 'adminconsole' || (isFlagUsable && flags.showAutoOpsPerfomanceChart)

  const baseTheme = useElasticChartsTheme()

  const { formatMessage } = useIntl()
  const {
    search_latency,
    search_rate,
    index_rate,
    index_latency,
    search_vcu,
    ingest_vcu,
    ml_vcu,
    storage_retained,
  } = data
  const hasSearchLatency = search_latency !== undefined && search_latency.error === null
  const hasSearchRate = search_rate !== undefined && search_rate.error === null
  const hasIndexLatency = index_latency !== undefined && index_latency.error === null
  const hasIndexRate = index_rate !== undefined && index_rate.error === null
  const hasSearchVCU = search_vcu !== undefined && search_vcu.error === null
  const hasIngestVCU = ingest_vcu !== undefined && ingest_vcu.error === null
  const hasMlVCU = ml_vcu !== undefined && ml_vcu.error === null
  const hasStorage = storage_retained !== undefined && storage_retained.error === null
  const refSearch = useRef<Chart>(null)
  const refIndex = useRef<Chart>(null)
  const refVCU = useRef<Chart>(null)
  const refStorage = useRef<Chart>(null)
  const searchCallout = generateCallout('search', [search_latency, search_rate])
  const indexCallout = generateCallout('index', [index_latency, index_rate])
  const vcuCallout = generateCallout('vcu', [search_vcu, ingest_vcu, ml_vcu])
  const storageCallout = generateCallout('storage_retained', [storage_retained])

  const pointerUpdate = (event: PointerEvent) => {
    refSearch.current?.dispatchExternalPointerEvent(event)
    refIndex.current?.dispatchExternalPointerEvent(event)
    refVCU.current?.dispatchExternalPointerEvent(event)
    refStorage.current?.dispatchExternalPointerEvent(event)
  }

  const IndexChart = () => {
    const { min: rateMin, max: rateMax } = calculateDomain(index_rate)
    const { min: latencyMin, max: latencyMax } = calculateDomain(index_latency)

    return (
      <EuiFlexItem>
        <EuiSplitPanel.Outer grow={true} hasBorder={true}>
          <EuiSplitPanel.Inner>
            <MetricTitle
              title='Indexing Rate / Indexing Latency'
              infoContent={
                <div>
                  <strong>Indexing rate</strong>
                  <p>Total number of documents indexed per second at the project level.</p>
                  <EuiSpacer size='m' />
                  <strong>Indexing latency</strong>
                  <p>
                    Peak project-level indexing latency, which is the time it takes to index all
                    incoming documents divided by their count in the given timeframe.
                  </p>
                </div>
              }
            />
            <EuiSpacer size='xs' />
            <Chart size={{ height: '200px' }} ref={refIndex}>
              <Settings
                baseTheme={baseTheme}
                noResults={
                  <ChartCallout
                    type={indexCallout.type}
                    title={formatMessage(indexCallout.title)}
                    message={indexCallout.message}
                  />
                }
                showLegend={false}
                pointerUpdateDebounce={0}
                onPointerUpdate={pointerUpdate}
              />

              <Tooltip
                headerFormatter={(pointerValue) => timeFormatterFn(pointerValue.value, from)}
              />

              <Axis id='bottom' position={Position.Bottom} showOverlappingTicks={true} />

              <Axis
                id='rate'
                groupId='rate'
                title='Indexing Rate'
                domain={{
                  min: rateMin,
                  max: rateMax,
                }}
                style={yAxisStyle}
                position={Position.Left}
                tickFormat={(d) => `${Number(d).toFixed(2)}/sec`}
              />

              <Axis
                id='latency'
                groupId='latency'
                title='Indexing Latency'
                domain={{
                  min: latencyMin,
                  max: latencyMax,
                }}
                style={yAxisStyle}
                position={Position.Right}
                tickFormat={(d) => `${Number(d).toFixed(2)} ms`}
              />

              {hasIndexRate && (
                <LineSeries
                  key='Indexing Rate'
                  id='Indexing Rate'
                  groupId='rate'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={index_rate.data ?? []}
                />
              )}
              {hasIndexLatency && (
                <LineSeries
                  key='Indexing Latency'
                  id='Indexing Latency'
                  groupId='latency'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={index_latency.data ?? []}
                />
              )}
            </Chart>
          </EuiSplitPanel.Inner>
        </EuiSplitPanel.Outer>
      </EuiFlexItem>
    )
  }

  const SearchChart = () => {
    const { min: rateMin, max: rateMax } = calculateDomain(search_rate)
    const { min: latencyMin, max: latencyMax } = calculateDomain(search_latency)

    return (
      <EuiFlexItem>
        <EuiSplitPanel.Outer grow={true} hasBorder={true}>
          <EuiSplitPanel.Inner>
            <MetricTitle
              title='Search Rate / Search Latency'
              infoContent={
                <div>
                  <strong>Search rate</strong>
                  <p>Total number of search requests executed per second at the project level.</p>
                  <EuiSpacer size='m' />
                  <strong>Search latency</strong>
                  <p>
                    Peak project-level search latency, which is the time it takes to execute all
                    incoming search requests divided by their count in the given timeframe.
                  </p>
                </div>
              }
            />
            <EuiSpacer size='xs' />
            <Chart size={{ height: '200px' }} ref={refSearch}>
              <Settings
                baseTheme={baseTheme}
                noResults={
                  <ChartCallout
                    type={searchCallout.type}
                    title={formatMessage(searchCallout.title)}
                    message={searchCallout.message}
                  />
                }
                showLegend={false}
                pointerUpdateDebounce={0}
                onPointerUpdate={pointerUpdate}
              />

              <Tooltip
                headerFormatter={(pointerValue) => timeFormatterFn(pointerValue.value, from)}
              />

              <Axis id='bottom' position={Position.Bottom} showOverlappingTicks={true} />

              <Axis
                id='rate'
                groupId='rate'
                title='Search Rate'
                domain={{
                  min: rateMin,
                  max: rateMax,
                }}
                style={yAxisStyle}
                position={Position.Left}
                tickFormat={(d) => `${Number(d).toFixed(2)}/sec`}
              />

              <Axis
                id='latency'
                groupId='latency'
                title='Search Latency'
                domain={{
                  min: latencyMin,
                  max: latencyMax,
                }}
                style={yAxisStyle}
                position={Position.Right}
                tickFormat={(d) => `${Number(d).toFixed(2)} ms`}
              />

              {hasSearchRate && (
                <LineSeries
                  key='Search Rate'
                  id='Search Rate'
                  groupId='rate'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={search_rate.data ?? []}
                />
              )}
              {hasSearchLatency && (
                <LineSeries
                  key='Search Latency'
                  id='Search Latency'
                  groupId='latency'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={search_latency.data ?? []}
                />
              )}
            </Chart>
          </EuiSplitPanel.Inner>
        </EuiSplitPanel.Outer>
      </EuiFlexItem>
    )
  }

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='l'>
        <EuiFlexItem>
          <EuiSplitPanel.Outer grow={true} hasBorder={true}>
            <EuiSplitPanel.Inner>
              <MetricTitle
                title='VCUs'
                infoContent='Amount of Virtual Compute Units (1 VCU contains 1GB of RAM and corresponding vCPU and local storage) allocated to each tier.'
              />
              <EuiSpacer size='xs' />
              <Chart size={{ height: '200px' }} ref={refVCU}>
                <Settings
                  baseTheme={baseTheme}
                  noResults={
                    <ChartCallout
                      type={vcuCallout.type}
                      title={formatMessage(vcuCallout.title)}
                      message={vcuCallout.message}
                    />
                  }
                  showLegend={true}
                  legendPosition={Position.Right}
                  pointerUpdateDebounce={0}
                  onPointerUpdate={pointerUpdate}
                />

                <TruncatedTooltip units='VCUs' />

                <Axis
                  id='bottom'
                  position={Position.Bottom}
                  tickFormat={(value) => timeFormatterFn(value, from)}
                />

                <Axis id='left' position={Position.Left} />

                {hasSearchVCU && (
                  <LineSeries
                    key='Search'
                    id='Search'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={search_vcu.data ?? []}
                  />
                )}
                {hasIngestVCU && (
                  <LineSeries
                    key='Ingest'
                    id='Ingest'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={ingest_vcu.data ?? []}
                  />
                )}
                {hasMlVCU && (
                  <LineSeries
                    key='ML'
                    id='ML'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={ml_vcu.data ?? []}
                  />
                )}
              </Chart>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiSplitPanel.Outer grow={true} hasBorder={true}>
            <EuiSplitPanel.Inner>
              <MetricTitle
                title='Storage retained'
                infoContent='Amount of data that is being retained in long-term object storage (subject to data retention settings).'
              />
              <EuiSpacer size='xs' />
              <Chart size={{ height: '200px' }} ref={refStorage}>
                <Settings
                  baseTheme={baseTheme}
                  noResults={
                    <ChartCallout
                      type={storageCallout.type}
                      title={formatMessage(storageCallout.title)}
                      message={storageCallout.message}
                    />
                  }
                  legendPosition={Position.Right}
                  pointerUpdateDebounce={0}
                  onPointerUpdate={pointerUpdate}
                />

                <TruncatedTooltip units='GB' />

                <Axis
                  id='bottom'
                  position={Position.Bottom}
                  tickFormat={(value) => timeFormatterFn(value, from)}
                />

                <Axis id='left' position={Position.Left} tickFormat={(d) => `${d} GB`} />

                {hasStorage && (
                  <AreaSeries
                    key='Storage retained'
                    id='Storage retained'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={(storage_retained.data ?? []).map(([x, y]) => [x, bytesToGigabytes(y)])}
                  />
                )}
              </Chart>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
      </EuiFlexGroup>
      {isActivated && (
        <Fragment>
          <EuiSpacer size='l' />
          <EuiFlexGroup gutterSize='l'>
            <IndexChart />
            <SearchChart />
          </EuiFlexGroup>
        </Fragment>
      )}
    </Fragment>
  )
}

export default ElasticsearchCharts
